import { Button } from "@/components/ui/button";
import IDENTIFIERS from "@/constants/test_id";
import { X } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { usePanelLogger } from "@/services/logger/logger-hooks";
import type {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { LogEventTypeEnum } from "@/services/logger/logger-types";
import { loggerContextManager } from "@/services/logger/smart-context-provider";
import { capitalizeInitialLetter } from "@/lib/string-util";

type RightSidebarProps = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  backdropOpacity?: number; // Range: 0 to 1
  onBackdropClick?: () => void;
  /**
   * Drops the panel and its backdrop 30px from the top of the viewport instead
   * of running the full height, so the top of the app header stays visible.
   */
  startBelowHeader?: boolean;
  /**
   * Makes the body a column that fills the panel instead of growing with its
   * content, so children can claim the leftover height with `flex-1`. Use it
   * for a panel whose content should be as tall as the panel — a map, a chart
   * — rather than a document that scrolls.
   */
  fillBody?: boolean;
  logOptions?: {
    enabled?: boolean;
    panelTitleBase?: string;
    context?: Record<string, unknown>;
    type?: LogEventTypeEnum;
    tag?: LogTags;
    category?: LogCategories;
    subCategory?: LogSubCategories;
  };
  children: React.ReactNode;
};

const RightSidebar: React.FC<RightSidebarProps> = ({
  title,
  isOpen,
  onClose,
  backdropOpacity = 0.5,
  onBackdropClick,
  startBelowHeader = false,
  fillBody = false,
  logOptions,
  children,
}) => {
  const { logPanelOpen, logPanelClose } = usePanelLogger();
  const enteredAtRef = useRef<number | null>(null);
  const hasLoggedCloseRef = useRef<boolean>(false);
  const hasLoggedOpenRef = useRef<boolean>(false);
  const prevIsOpenRef = useRef<boolean>(isOpen);

  const shouldLog = logOptions?.enabled ?? true;
  const panelTitleBase = capitalizeInitialLetter(
    logOptions?.panelTitleBase ?? title,
  );

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("no-scroll");
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    loggerContextManager.setPanelContext({
      panelName: panelTitleBase,
      ...(logOptions?.tag ? { tag: logOptions.tag } : {}),
      ...(logOptions?.category ? { category: logOptions.category } : {}),
      ...(logOptions?.subCategory
        ? { subCategory: logOptions.subCategory }
        : {}),
    });

    return () => {
      loggerContextManager.clearPanelContext();
    };
  }, [
    isOpen,
    panelTitleBase,
    logOptions?.tag,
    logOptions?.category,
    logOptions?.subCategory,
  ]);

  useEffect(() => {
    const now = Date.now();

    if (isOpen && !hasLoggedOpenRef.current) {
      enteredAtRef.current = now;
      hasLoggedCloseRef.current = false;
      hasLoggedOpenRef.current = true;

      if (shouldLog) {
        logPanelOpen(
          panelTitleBase,
          {
            panelName: panelTitleBase,
            ...(logOptions?.context ?? {}),
          },
          {
            type: logOptions?.type ?? LogEventTypeEnum.PAGE_VIEW,
            tag: logOptions?.tag,
            category: logOptions?.category,
            subCategory: logOptions?.subCategory,
          },
        );
      }
    }

    if (!isOpen && prevIsOpenRef.current && !hasLoggedCloseRef.current) {
      const duration = enteredAtRef.current ? now - enteredAtRef.current : 0;
      if (shouldLog) {
        logPanelClose(
          panelTitleBase,
          {
            panelName: panelTitleBase,
            ...(logOptions?.context ?? {}),
          },
          {
            type: logOptions?.type ?? LogEventTypeEnum.PAGE_VIEW,
            tag: logOptions?.tag,
            category: logOptions?.category,
            subCategory: logOptions?.subCategory,
            duration,
          },
        );
      }
      hasLoggedCloseRef.current = true;
      enteredAtRef.current = null;
      hasLoggedOpenRef.current = false;
    }

    prevIsOpenRef.current = isOpen;
  }, [
    isOpen,
    logPanelClose,
    logPanelOpen,
    panelTitleBase,
    shouldLog,
    title,
    logOptions,
  ]);

  const closeWithLog = () => {
    const now = Date.now();
    if (shouldLog && !hasLoggedCloseRef.current && isOpen) {
      const duration = enteredAtRef.current ? now - enteredAtRef.current : 0;
      logPanelClose(
        panelTitleBase,
        {
          panelName: panelTitleBase,
          ...(logOptions?.context ?? {}),
        },
        {
          type: logOptions?.type ?? LogEventTypeEnum.USER_INTERACTION,
          tag: logOptions?.tag,
          category: logOptions?.category,
          subCategory: logOptions?.subCategory,
          duration,
        },
      );
      hasLoggedCloseRef.current = true;
      enteredAtRef.current = null;
      hasLoggedOpenRef.current = false;
    }
    onClose();
  };

  const handleBackdropClick = () => {
    if (onBackdropClick) {
      onBackdropClick();
      return;
    }
    closeWithLog();
  };

  return (
    <>
      {/*
        Backdrop. 2.1.1 Keyboard — A: this is a click-outside-to-dismiss affordance, not a
        control — it duplicates the labelled Close button below and must not appear in the
        tab order or be announced, so it is marked presentational rather than converted to
        a button. Keyboard users dismiss the panel with the Close button.
      */}
      <div
        role="presentation"
        className={`fixed z-[90] bg-black transition-opacity duration-300 ease-in-out ${
          startBelowHeader ? "inset-x-0 bottom-0 top-[-32px]" : "inset-0"
        } ${
          isOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
        style={{ backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})` }}
        onClick={handleBackdropClick}
      />

      {/* Sidebar */}
      <div
        className={`fixed right-0 z-[100] transform border-l bg-background shadow-lg transition-all duration-500 ease-in-out sm:w-[640px] ${
          startBelowHeader
            ? "bottom-0 top-[-32px] w-full"
            : "inset-y-0 size-full"
        } ${
          // Filling means the panel itself must not scroll — the body below
          // takes the overflow, or a `flex-1` child would be sized by its
          // content instead of by the panel.
          fillBody ? "flex flex-col overflow-hidden" : "overflow-y-auto"
        } ${
          isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        }`}
      >
        <div className="shrink-0 border-b p-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{title}</h3>
            {/* 4.1.2 Name, Role, Value — A: icon-only, so it was announced as just
                "button" with no indication that it closes the panel. */}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Close panel"
              onClick={() => closeWithLog()}
              data-testid={IDENTIFIERS.RIGHT_SIDEBAR.CLOSE_BUTTON}
            >
              <X aria-hidden="true" className="size-4" />
            </Button>
          </div>
        </div>
        {fillBody ? (
          // `min-h-0` so a tall child can shrink to the panel rather than
          // pushing the column past it.
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </>
  );
};

export default RightSidebar;
